<template>
  <div class="settings-container">
    <PageHeader 
      pageName="Paramètres" 
      pageTitle="Paramètres du compte" 
      pageSubtitle="Gérez vos préférences, votre sécurité et vos informations de facturation."
    />

    <!-- Onglets de navigation -->
    <div class="tabs-nav">
      <button 
        v-for="tab in tabs" 
        :key="tab.id"
        class="tab-btn"
        :class="{ 'active': activeTab === tab.id }"
        @click="activeTab = tab.id"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path :d="tab.icon"></path>
        </svg>
        {{ tab.label }}
      </button>
    </div>

    <!-- Contenu des onglets -->
    <div class="tab-content">
      <!-- Onglet Général -->
      <div v-if="activeTab === 'general'" class="tab-panel">
        <div class="settings-section">
          <h3>Informations personnelles</h3>
          <div class="form-grid">
            <div class="form-group">
              <label>Nom complet</label>
              <input type="text" v-model="settings.general.name" placeholder="Votre nom complet">
            </div>
            <div class="form-group">
              <label>Email</label>
              <input type="email" v-model="settings.general.email" placeholder="votre@email.com">
            </div>
            <div class="form-group">
              <label>Téléphone</label>
              <input type="tel" v-model="settings.general.phone" placeholder="+33 1 23 45 67 89">
            </div>
            <div class="form-group">
              <label>Langue</label>
              <select v-model="settings.general.language">
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>
          </div>
        </div>

        <div class="settings-section">
          <h3>Préférences</h3>
          <div class="toggle-group">
            <div class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-title">Notifications email</span>
                <span class="toggle-desc">Recevoir des notifications par email</span>
              </div>
              <label class="toggle">
                <input type="checkbox" v-model="settings.general.emailNotifications">
                <span class="slider"></span>
              </label>
            </div>
            <div class="toggle-item">
              <div class="toggle-info">
                <span class="toggle-title">Mode sombre</span>
                <span class="toggle-desc">Interface en mode sombre</span>
              </div>
              <label class="toggle">
                <input type="checkbox" v-model="settings.general.darkMode">
                <span class="slider"></span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Onglet Sécurité -->
      <div v-if="activeTab === 'security'" class="tab-panel">
        <div class="settings-section">
          <h3>Authentification</h3>
          <div class="security-item">
            <div class="security-info">
              <div class="security-title">Mot de passe</div>
              <div class="security-desc">Dernière modification il y a 30 jours</div>
            </div>
            <button class="btn btn-secondary" @click="changePassword">Modifier</button>
          </div>
          
          <div class="security-item">
            <div class="security-info">
              <div class="security-title">Authentification à deux facteurs</div>
              <div class="security-desc">Sécurisez votre compte avec 2FA</div>
            </div>
            <div class="security-status">
              <span class="status-badge enabled">Activé</span>
              <button class="btn btn-small" @click="manage2FA">Gérer</button>
            </div>
          </div>
        </div>

        <div class="settings-section">
          <h3>Sessions actives</h3>
          <div class="sessions-list">
            <div v-for="session in activeSessions" :key="session.id" class="session-item">
              <div class="session-info">
                <div class="session-device">{{ session.device }}</div>
                <div class="session-location">{{ session.location }}</div>
                <div class="session-time">{{ session.lastActive }}</div>
              </div>
              <button v-if="!session.current" class="btn btn-danger" @click="revokeSession(session.id)">
                Révoquer
              </button>
              <span v-else class="current-session">Session actuelle</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Onglet Notifications -->
      <div v-if="activeTab === 'notifications'" class="tab-panel">
        <div class="settings-section">
          <h3>Préférences de notification</h3>
          <div class="notification-groups">
            <div v-for="group in notificationGroups" :key="group.id" class="notification-group">
              <h4>{{ group.title }}</h4>
              <div class="notification-options">
                <div v-for="option in group.options" :key="option.id" class="notification-option">
                  <div class="option-info">
                    <span class="option-title">{{ option.title }}</span>
                    <span class="option-desc">{{ option.description }}</span>
                  </div>
                  <div class="option-controls">
                    <label class="toggle">
                      <input type="checkbox" v-model="option.email">
                      <span class="slider"></span>
                    </label>
                    <span class="control-label">Email</span>
                    <label class="toggle">
                      <input type="checkbox" v-model="option.push">
                      <span class="slider"></span>
                    </label>
                    <span class="control-label">Push</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Onglet Facturation -->
      <div v-if="activeTab === 'billing'" class="tab-panel">
        <div class="settings-section">
          <h3>Plan actuel</h3>
          <div class="plan-info">
            <div class="plan-details">
              <h4>Plan Premium</h4>
              <p>Accès complet à toutes les fonctionnalités</p>
              <div class="plan-price">29,99€ / mois</div>
            </div>
            <div class="plan-actions">
              <button class="btn btn-primary">Gérer l'abonnement</button>
              <button class="btn btn-secondary">Changer de plan</button>
            </div>
          </div>
        </div>

        <div class="settings-section">
          <h3>Historique de facturation</h3>
          <div class="billing-history">
            <div v-for="invoice in billingHistory" :key="invoice.id" class="invoice-item">
              <div class="invoice-info">
                <div class="invoice-date">{{ invoice.date }}</div>
                <div class="invoice-amount">{{ invoice.amount }}</div>
              </div>
              <div class="invoice-actions">
                <span class="status-badge" :class="invoice.status">{{ invoice.status }}</span>
                <button class="btn btn-small">Télécharger</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Onglet Confidentialité -->
      <div v-if="activeTab === 'privacy'" class="tab-panel">
        <div class="settings-section">
          <h3>Données personnelles</h3>
          <div class="privacy-options">
            <div class="privacy-item">
              <div class="privacy-info">
                <span class="privacy-title">Visibilité du profil</span>
                <span class="privacy-desc">Qui peut voir votre profil</span>
              </div>
              <select v-model="settings.privacy.profileVisibility">
                <option value="public">Public</option>
                <option value="private">Privé</option>
                <option value="contacts">Contacts uniquement</option>
              </select>
            </div>
            
            <div class="privacy-item">
              <div class="privacy-info">
                <span class="privacy-title">Analyse des données</span>
                <span class="privacy-desc">Autoriser l'analyse pour améliorer les services</span>
              </div>
              <label class="toggle">
                <input type="checkbox" v-model="settings.privacy.analytics">
                <span class="slider"></span>
              </label>
            </div>
          </div>
        </div>

        <div class="settings-section danger-zone">
          <h3>Zone de danger</h3>
          <div class="danger-actions">
            <div class="danger-item">
              <div class="danger-info">
                <span class="danger-title">Exporter les données</span>
                <span class="danger-desc">Télécharger toutes vos données</span>
              </div>
              <button class="btn btn-secondary">Exporter</button>
            </div>
            
            <div class="danger-item">
              <div class="danger-info">
                <span class="danger-title">Supprimer le compte</span>
                <span class="danger-desc">Action irréversible</span>
              </div>
              <button class="btn btn-danger" @click="deleteAccount">Supprimer</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Boutons de sauvegarde -->
    <div class="settings-footer">
      <button class="btn btn-secondary" @click="resetSettings">Annuler</button>
      <button class="btn btn-primary" @click="saveSettings">Sauvegarder les modifications</button>
    </div>
  </div>
</template>

<script>
import { consoleLog } from '@/utils/logger';

export default {
  name: 'AccountSettingsView',
  components: {
    PageHeader: () => import('@/components/PageHeader.vue')
  },
  data() {
    return {
      activeTab: 'general',
      tabs: [
        {
          id: 'general',
          label: 'Général',
          icon: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'
        },
        {
          id: 'security',
          label: 'Sécurité',
          icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'
        },
        {
          id: 'notifications',
          label: 'Notifications',
          icon: 'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0'
        },
        {
          id: 'billing',
          label: 'Facturation',
          icon: 'M3 3h18v18H3zM8 12h8M8 8h8M8 16h4'
        },
        {
          id: 'privacy',
          label: 'Confidentialité',
          icon: 'M12 15v2m-6 4h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2zm10-10V7a4 4 0 0 0-8 0v4h8z'
        }
      ],
      settings: {
        general: {
          name: '',
          email: '',
          phone: '',
          language: 'fr',
          emailNotifications: true,
          darkMode: false
        },
        privacy: {
          profileVisibility: 'public',
          analytics: true
        }
      },
      activeSessions: [
        {
          id: 1,
          device: 'Chrome sur Windows',
          location: 'Paris, France',
          lastActive: 'Maintenant',
          current: true
        },
        {
          id: 2,
          device: 'Safari sur iPhone',
          location: 'Paris, France',
          lastActive: 'Il y a 2 heures',
          current: false
        }
      ],
      notificationGroups: [
        {
          id: 'account',
          title: 'Compte',
          options: [
            {
              id: 'login',
              title: 'Nouvelle connexion',
              description: 'Quand quelqu\'un se connecte à votre compte',
              email: true,
              push: true
            },
            {
              id: 'password',
              title: 'Changement de mot de passe',
              description: 'Quand votre mot de passe est modifié',
              email: true,
              push: false
            }
          ]
        },
        {
          id: 'chatbot',
          title: 'Chatbots',
          options: [
            {
              id: 'new_message',
              title: 'Nouveaux messages',
              description: 'Quand vos chatbots reçoivent des messages',
              email: false,
              push: true
            },
            {
              id: 'errors',
              title: 'Erreurs',
              description: 'Quand vos chatbots rencontrent des erreurs',
              email: true,
              push: true
            }
          ]
        }
      ],
      billingHistory: [
        {
          id: 1,
          date: '1 Dec 2024',
          amount: '29,99€',
          status: 'paid'
        },
        {
          id: 2,
          date: '1 Nov 2024',
          amount: '29,99€',
          status: 'paid'
        }
      ]
    };
  },
  mounted() {
    this.loadSettings();
    // Gérer les paramètres d'URL pour l'onglet actif
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    if (tab && this.tabs.find(t => t.id === tab)) {
      this.activeTab = tab;
    }
  },
  methods: {
    loadSettings() {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData).data || JSON.parse(userData);
        this.settings.general.name = user.name || '';
        this.settings.general.email = user.email || '';
        this.settings.general.phone = user.phone || '';
      }
    },
    saveSettings() {
      // Sauvegarder les paramètres
      const userData = JSON.parse(localStorage.getItem('user'));
      if (userData.data) {
        userData.data = { ...userData.data, ...this.settings.general };
      } else {
        Object.assign(userData, this.settings.general);
      }
      localStorage.setItem('user', JSON.stringify(userData));
      
      this.showNotification('Paramètres sauvegardés avec succès', 'success');
    },
    resetSettings() {
      this.loadSettings();
      this.showNotification('Modifications annulées', 'info');
    },
    changePassword() {
      // Implémenter la logique de changement de mot de passe
      consoleLog('Changer le mot de passe');
    },
    manage2FA() {
      consoleLog('Gérer 2FA');
    },
    revokeSession(sessionId) {
      this.activeSessions = this.activeSessions.filter(s => s.id !== sessionId);
      this.showNotification('Session révoquée', 'success');
    },
    deleteAccount() {
      if (confirm('Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible.')) {
        consoleLog('Supprimer le compte');
      }
    },
    showNotification(message, type = 'success') {
      consoleLog(`${type}: ${message}`);
    }
  }
};
</script>

<style scoped>
.settings-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
  background: #f8f9fa;
  min-height: 100vh;
}

/* Header styles removed in favor of PageHeader component */

.tabs-nav {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid #e2e8f0;
  overflow-x: auto;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: none;
  border: none;
  color: #718096;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.tab-btn:hover {
  color: #4a5568;
}

.tab-btn.active {
  color: #667eea;
  border-bottom-color: #667eea;
}

.tab-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  padding: 2rem;
  margin-bottom: 2rem;
}

.settings-section {
  margin-bottom: 2rem;
}

.settings-section:last-child {
  margin-bottom: 0;
}

.settings-section h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 1.5rem 0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 0.5rem;
}

.form-group input,
.form-group select {
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.toggle-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.toggle-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f7fafc;
  border-radius: 12px;
}

.toggle-info {
  display: flex;
  flex-direction: column;
}

.toggle-title {
  font-weight: 500;
  color: #1a202c;
}

.toggle-desc {
  font-size: 0.875rem;
  color: #718096;
}

.toggle {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e0;
  transition: 0.4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #667eea;
}

input:checked + .slider:before {
  transform: translateX(24px);
}

.security-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 1rem;
}

.security-info {
  flex: 1;
}

.security-title {
  font-weight: 500;
  color: #1a202c;
  margin-bottom: 0.25rem;
}

.security-desc {
  font-size: 0.875rem;
  color: #718096;
}

.security-status {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.status-badge {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-badge.enabled {
  background: #e6fffa;
  color: #319795;
}

.status-badge.paid {
  background: #e6fffa;
  color: #319795;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a67d8;
}

.btn-secondary {
  background: white;
  color: #4a5568;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #f7fafc;
}

.btn-danger {
  background: #e53e3e;
  color: white;
}

.btn-danger:hover {
  background: #c53030;
}

.btn-small {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.settings-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.danger-zone {
  border: 1px solid #fed7d7;
  border-radius: 12px;
  padding: 1.5rem;
  background: #fffafa;
}

.danger-zone h3 {
  color: #e53e3e;
}

@media (max-width: 768px) {
  .settings-container {
    padding: 1rem;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .tabs-nav {
    flex-wrap: wrap;
  }
  
  .toggle-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}
</style> 